import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Phone, Mail, User, Eye, Calendar, TrendingUp, Clock, CheckCircle, XCircle, MessageSquare, Video, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PIPELINE_STAGES = [
  { id: 'nuovo', label: 'Nuovo', color: 'bg-blue-50 border-blue-300', headerColor: 'bg-blue-500' },
  { id: 'messaggio_whatsapp', label: 'WhatsApp', color: 'bg-green-50 border-green-300', headerColor: 'bg-green-500' },
  { id: 'telefonata_target', label: 'Telefonata', color: 'bg-amber-50 border-amber-300', headerColor: 'bg-amber-500' },
  { id: 'videochiamata', label: 'Videochiamata', color: 'bg-indigo-50 border-indigo-300', headerColor: 'bg-indigo-500' },
  { id: 'convertito', label: 'Convertito', color: 'bg-emerald-50 border-emerald-300', headerColor: 'bg-emerald-500' },
  { id: 'perso', label: 'Perso', color: 'bg-slate-100 border-slate-300', headerColor: 'bg-slate-500' }
];

export default function AdminMonitor() {
  const { token } = useAuth();
  const [salesUsers, setSalesUsers] = useState([]);
  const [selectedSales, setSelectedSales] = useState('all');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [stats, setStats] = useState({ total: 0, inProgress: 0, converted: 0, lost: 0 });

  useEffect(() => {
    fetchSalesUsers();
  }, []);

  useEffect(() => {
    if (salesUsers.length > 0) {
      fetchLeads();
    }
  }, [selectedSales, salesUsers]);

  const fetchSalesUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/sales-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesUsers(response.data);
      if (response.data.length > 0) {
        setSelectedSales(response.data[0].email);
      }
    } catch (error) {
      toast.error('Errore nel caricamento commerciali');
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/leads?view_all=true`;
      if (selectedSales !== 'all') {
        url += `&assigned_to=${selectedSales}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeads(response.data);
      
      // Calcola statistiche
      const total = response.data.length;
      const converted = response.data.filter(l => l.status === 'convertito').length;
      const lost = response.data.filter(l => l.status === 'perso').length;
      const inProgress = total - converted - lost;
      
      setStats({ total, inProgress, converted, lost });
    } catch (error) {
      toast.error('Errore nel caricamento lead');
    } finally {
      setLoading(false);
    }
  };

  const getLeadsByStage = (stageId) => {
    return leads.filter((lead) => lead.status === stageId);
  };

  const getSelectedSalesName = () => {
    if (selectedSales === 'all') return 'Tutti i Commerciali';
    const user = salesUsers.find(u => u.email === selectedSales);
    return user?.name || selectedSales;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLostReasonLabel = (reason) => {
    const labels = {
      'non_in_target': 'Non in target',
      'budget_insufficiente': 'Budget insufficiente',
      'tempistiche_non_allineate': 'Tempistiche non allineate',
      'ha_scelto_competitor': 'Ha scelto competitor',
      'non_interessato': 'Non interessato',
      'non_raggiungibile': 'Non raggiungibile',
      'mancanza_requisiti': 'Mancanza requisiti',
      'altro': 'Altro'
    };
    return labels[reason] || reason;
  };

  if (loading && salesUsers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <div className="p-6 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <Eye className="h-8 w-8 text-indigo-600" />
              Monitor Commerciali
            </h1>
            <p className="text-slate-600">Visualizza la pipeline e il lavoro di ogni commerciale</p>
          </div>
          
          <div className="flex items-center gap-4">
            <Select value={selectedSales} onValueChange={setSelectedSales}>
              <SelectTrigger className="w-64 h-11 bg-white">
                <SelectValue placeholder="Seleziona commerciale" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">👥 Tutti i Commerciali</SelectItem>
                {salesUsers.map((user) => (
                  <SelectItem key={user.email} value={user.email}>
                    👤 {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats del commerciale selezionato */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="p-4 bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-xs font-medium">Lead Totali</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-indigo-200" />
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-xs font-medium">In Lavorazione</p>
                <p className="text-2xl font-bold">{stats.inProgress}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-200" />
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-xs font-medium">Convertiti</p>
                <p className="text-2xl font-bold">{stats.converted}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-emerald-200" />
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-slate-500 to-slate-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-100 text-xs font-medium">Persi</p>
                <p className="text-2xl font-bold">{stats.lost}</p>
              </div>
              <XCircle className="h-8 w-8 text-slate-200" />
            </div>
          </Card>
        </div>
      </div>

      {/* Pipeline del commerciale selezionato */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden bg-slate-50">
        <div className="flex gap-4 p-6 h-full items-start min-w-max">
          {PIPELINE_STAGES.map((stage) => {
            const stageLeads = getLeadsByStage(stage.id);
            return (
              <div
                key={stage.id}
                className={`flex-shrink-0 w-72 ${stage.color} rounded-xl border flex flex-col max-h-full`}
              >
                <div className={`p-3 ${stage.headerColor} rounded-t-xl`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-white text-sm">{stage.label}</h3>
                    <span className="bg-white/20 text-white text-xs font-semibold px-2 py-1 rounded-full">
                      {stageLeads.length}
                    </span>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {stageLeads.length === 0 ? (
                    <div className="text-center text-slate-400 text-xs py-6">
                      Nessun lead
                    </div>
                  ) : (
                    stageLeads.map((lead) => (
                      <Card
                        key={lead.id}
                        className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer"
                        onClick={() => setSelectedLead(lead)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-slate-900 text-sm">{lead.name}</h4>
                          {selectedSales === 'all' && (
                            <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                              {lead.assigned_to_name?.split(' ')[0]}
                            </span>
                          )}
                        </div>
                        <div className="space-y-1 text-xs text-slate-500">
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            <span className="truncate">{lead.email}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            <span>{lead.phone}</span>
                          </div>
                          <div className="flex items-center gap-1 text-slate-400">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(lead.updated_at)}</span>
                          </div>
                        </div>
                        {/* Indicatori */}
                        <div className="flex gap-1 mt-2">
                          {lead.notes && (
                            <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                            </span>
                          )}
                          {lead.video_call_info?.note_videochiamata && (
                            <span className="text-xs bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded flex items-center gap-1">
                              <Video className="h-3 w-3" />
                            </span>
                          )}
                          {lead.lost_reason && (
                            <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                            </span>
                          )}
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal dettaglio lead */}
      <Dialog open={selectedLead !== null} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Dettaglio Lead</DialogTitle>
          </DialogHeader>

          {selectedLead && (
            <div className="space-y-4">
              {/* Info base */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-xs text-slate-500">Nome</Label>
                  <p className="font-medium">{selectedLead.name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p className="font-medium">{selectedLead.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Telefono</Label>
                  <p className="font-medium">{selectedLead.phone}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Commerciale</Label>
                  <p className="font-medium">{selectedLead.assigned_to_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Creato</Label>
                  <p className="font-medium text-sm">{formatDate(selectedLead.created_at)}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Ultimo aggiornamento</Label>
                  <p className="font-medium text-sm">{formatDate(selectedLead.updated_at)}</p>
                </div>
              </div>

              {/* Candidatura */}
              {selectedLead.candidatura && Object.keys(selectedLead.candidatura).length > 0 && (
                <div className="border rounded-lg p-4">
                  <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    📋 Candidatura
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(selectedLead.candidatura).map(([q, a], i) => (
                      <div key={i} className="text-sm">
                        <p className="font-medium text-slate-700">{q}</p>
                        <p className="text-slate-600">{a || '-'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Note commerciale */}
              {selectedLead.notes && (
                <div className="border rounded-lg p-4 bg-blue-50">
                  <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                    Note Commerciale
                  </h3>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedLead.notes}</p>
                </div>
              )}

              {/* Note videochiamata */}
              {selectedLead.video_call_info?.note_videochiamata && (
                <div className="border rounded-lg p-4 bg-indigo-50">
                  <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <Video className="h-4 w-4 text-indigo-600" />
                    Note Videochiamata
                  </h3>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedLead.video_call_info.note_videochiamata}</p>
                </div>
              )}

              {/* Motivo perdita */}
              {selectedLead.status === 'perso' && selectedLead.lost_reason && (
                <div className="border rounded-lg p-4 bg-red-50">
                  <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    Motivo Perdita
                  </h3>
                  <p className="text-sm font-medium text-red-700">{getLostReasonLabel(selectedLead.lost_reason)}</p>
                  {selectedLead.lost_what_searching && (
                    <p className="text-sm text-red-600 mt-2">
                      <strong>Cosa cercava:</strong> {selectedLead.lost_what_searching}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
